import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
  Collapse,
  Tooltip,
  CircularProgress,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Add, Delete, ShoppingCart } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    background: "#fff",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
    position: "fixed",
    bottom: 72, // sobe um pouco para não colar no composer
    right: 8,
    left: 8,
    zIndex: 50,
    maxHeight: "55vh", // ocupa menos da tela em mobile
    overflowY: "auto",
    [theme.breakpoints.up("sm")]: {
      left: "auto",
      right: 24,
      width: 340,
      bottom: 160,
    },
  },
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
    background: "#f9fafb",
    padding: theme.spacing(0.75),
    borderRadius: 6,
    "& .MuiTypography-root": {
      fontSize: "0.85rem",
    },
  },
  total: {
    marginTop: theme.spacing(1.5),
    fontWeight: "bold",
    fontSize: 15,
    textAlign: "right",
  },
  addBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    "& .MuiTextField-root": {
      "& .MuiInputBase-root": {
        fontSize: "0.9rem",
      },
    },
  },
  fabButton: {
    position: "fixed",
    bottom: 96, // afasta mais do composer no mobile
    right: 16,
    zIndex: 99,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#25D366",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.55,
    transition: "opacity 0.25s ease, transform 0.25s ease",
    "&:hover": {
      opacity: 1,
      transform: "scale(1.07)",
      background: "#1ebe5d",
    },
    [theme.breakpoints.up("sm")]: {
      bottom: 90,
      right: 24,
    },
  },
  sectionTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
  },
  checkboxGroup: {
    display: "flex",
    justifyContent: "space-around",
    marginBottom: theme.spacing(0.5),
    "& .MuiFormControlLabel-root": {
      margin: 0,
      "& .MuiTypography-root": {
        fontSize: "0.85rem",
      },
    },
  },
  actionButton: {
    marginTop: theme.spacing(0.75),
    fontSize: "0.85rem",
    padding: theme.spacing(0.75),
    fontWeight: "bold",
  },
}));

export default function Carrinho({ chat, onMensagemEnviada, modoCliente }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  // Self-checkout fields
  const [nomeCliente, setNomeCliente] = useState("");
  const [telefoneCliente, setTelefoneCliente] = useState("");
  const [itens, setItens] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [cardapioDia, setCardapioDia] = useState(null);
  const [opcaoCardapio, setOpcaoCardapio] = useState("1");
  const [loading, setLoading] = useState(false);
  const [novoItem, setNovoItem] = useState({
    nome: "",
    quantidade: 1,
    preco: "",
    observacoes: "",
  });
  const [tipoPedido, setTipoPedido] = useState("retirada");
  const [endereco, setEndereco] = useState({ rua: "", numero: "", bairro: "" });
  const [pagamento, setPagamento] = useState({ forma: "", troco: "" });
  const [taxaEntrega, setTaxaEntrega] = useState(0);

  const opcoesCardapioValidas = [
    cardapioDia?.opcao1_nome,
    cardapioDia?.opcao2_nome,
  ].filter(Boolean);

  // 🔹 Carrega produtos
  useEffect(() => {
    if (open && produtos.length === 0) {
      setLoading(true);
      api
        .get("/produtos")
        .then((res) => {
          let data = res.data?.data || [];
          if (!Array.isArray(data)) data = [data];
          setProdutos(data);
        })
        .catch((err) => {
          console.error("❌ Erro ao carregar produtos:", err);
          toast.error("Erro ao carregar produtos");
        })
        .finally(() => setLoading(false));
    }
  }, [open, produtos.length]);

  // 🔹 Carrega cardápio do dia quando abrir o carrinho
  useEffect(() => {
    if (!open) return;

    const dias = [
      "Domingo",
      "Segunda",
      "Terça",
      "Quarta",
      "Quinta",
      "Sexta",
      "Sábado",
    ];

    const hoje = new Date();
    const diaLabel = dias[hoje.getDay()] || undefined;
    if (!diaLabel) return;

    api
      .get("/cardapio", { params: { dia: diaLabel } })
      .then((res) => {
        const data = res.data?.data;
        if (Array.isArray(data) && data.length > 0) {
          setCardapioDia(data[0]);
        } else {
          setCardapioDia(null);
        }
      })
      .catch((err) => {
        console.error("❌ Erro ao carregar cardápio do dia:", err);
        setCardapioDia(null);
      });
  }, [open]);

  // 🔹 Recalcula taxa de entrega
  useEffect(() => {
    if (tipoPedido !== "entrega") {
      setTaxaEntrega(0);
      return;
    }

    const bairro = (endereco.bairro || "").trim().toLowerCase();
    if (!bairro) return setTaxaEntrega(0);
    setTaxaEntrega(bairro.includes("centro") ? 1 : 2);
  }, [tipoPedido, endereco.bairro]);

  // 🔹 Selecionar produto
  const handleSelectProduto = (nomeProduto) => {
    const produto = produtos.find((p) => p.nome === nomeProduto);
    if (produto) {
      const preco =
        Number(produto.valor) ||
        Number(produto.preco) ||
        Number(produto.price) ||
        0;

      const nome = produto.nome.toLowerCase();
      const isMarmita = nome.includes("marmita") || nome.includes("quentinha");

      setNovoItem({
        nome: produto.nome,
        preco: preco.toFixed(2),
        quantidade: 1,
        observacoes: !isMarmita && produto.descricao ? produto.descricao : "",
      });
    } else {
      setNovoItem({ ...novoItem, nome: nomeProduto });
    }
  };

  const handleAdd = () => {
    if (!novoItem.nome || !novoItem.preco) {
      toast.error("Preencha nome e preço do item");
      return;
    }

    const opcaoEscolhida =
      opcoesCardapioValidas.length === 1
        ? "1"
        : opcaoCardapio || "1";

    const itemComOpcao = {
      ...novoItem,
      opcaoCardapio: opcaoEscolhida,
    };

    setItens([...itens, itemComOpcao]);
    setNovoItem({ nome: "", quantidade: 1, preco: "", observacoes: "" });
    // Mantém "1" como padrão após adicionar
    setOpcaoCardapio("1");
  };

  const handleRemove = (index) => setItens(itens.filter((_, i) => i !== index));

  const total = itens.reduce(
    (acc, i) => acc + Number(i.preco || 0) * Number(i.quantidade || 1),
    0
  );
  const totalComTaxa = total + taxaEntrega;

  // 📨 Envia resumo para confirmação
  const handleEnviarParaConfirmacao = async () => {
    if (itens.length === 0) return toast.warn("Carrinho vazio!");
    // Self-checkout: require name/phone
    if (modoCliente) {
      if (!nomeCliente.trim()) return toast.error("Nome é obrigatório!");
      if (!telefoneCliente.trim()) return toast.error("Telefone é obrigatório!");
    }

    const resumo = `
📦 *Resumo do pedido*  

${itens
        .map((i) => {
          const nomeOriginal = String(i.nome || "").trim();
          const nome = nomeOriginal
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

          let icone = "🍽️";
          if (nome.includes("marmita") || nome.includes("quentinha")) icone = "🍽️";
          else if (nome.includes("coca") || nome.includes("suco") || nome.includes("fanta") || nome.includes("agua"))
            icone = "🥤";
          else if (nome.includes("pudim") || nome.includes("bolo") || nome.includes("doce"))
            icone = "🍰";
          else if (nome.includes("lanche") || nome.includes("hamb") || nome.includes("xis"))
            icone = "🍔";
          else if (nome.includes("pizza")) icone = "🍕";

          const linhaOpcao = i.opcaoCardapio
            ? `Opção ${i.opcaoCardapio}  \n`
            : "";

          return `${icone} *${nomeOriginal}*  \n${linhaOpcao}Quantidade: ${i.quantidade}  \nPreço: R$ ${Number(i.preco).toFixed(2)}  \nObs: ${i.observacoes || "Nenhuma"}`;
        })
        .join("\n\n")}

${tipoPedido === "entrega" ? `🚚 *Taxa de entrega:* R$ ${taxaEntrega.toFixed(2)}` : ""}
💰 *Total:* R$ ${Number(totalComTaxa).toFixed(2)}

${tipoPedido === "entrega"
        ? `🏠 Endereço: ${endereco.rua}, ${endereco.numero}, ${endereco.bairro}`
        : ""}
💳 *Forma de Pagamento:* ${pagamento.forma}
${pagamento.forma === "Dinheiro" && pagamento.troco ? `\nTroco para: R$ ${pagamento.troco}` : ""}

${modoCliente ? `👤 Nome: ${nomeCliente}\n📱 Telefone: ${telefoneCliente}` : ""}

✅ *Confirma o pedido?*
`.trim();

    try {
      if (modoCliente) {
        // Self-checkout: send to backend selfcheckout endpoint
        await api.post(
          "/selfcheckout/pedido",
          {
            nome: nomeCliente,
            telefone: telefoneCliente,
            itens,
            total: totalComTaxa,
            tipo: tipoPedido,
            endereco: tipoPedido === "entrega" ? endereco : null,
            pagamento: {
              ...pagamento,
              taxaEntrega,
            },
            resumo,
          }
        );
        toast.success("Pedido enviado! Aguarde confirmação.");
        setItens([]);
        setNomeCliente("");
        setTelefoneCliente("");
        setEndereco({ rua: "", numero: "", bairro: "" });
        setPagamento({ forma: "", troco: "" });
        setTipoPedido("retirada");
        setOpen(false);
      } else {
        await api.post(
          "/chat/send/text",
          {
            Phone: chat.telefone,
            Body: resumo,
            nome: chat.nome,
          },
          {
            headers: { "Content-Type": "application/json; charset=utf-8" },
            transformRequest: [(data) => new TextEncoder().encode(JSON.stringify(data))],
          }
        );
        toast.info("Resumo enviado para o cliente confirmar!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar pedido.");
    }
  };

  const handleFecharPedido = async () => {
    if (itens.length === 0) return toast.warn("Carrinho vazio!");

    try {
      await api.post("/pedidos", {
        chatId: chat.id,
        itens,
        total: totalComTaxa,
        tipo: tipoPedido,
        endereco: tipoPedido === "entrega" ? endereco : null,
        pagamento: {
          ...pagamento,
          taxaEntrega,
        },
      });
      toast.success("Pedido criado e chat finalizado!");
      setItens([]);
      setEndereco({ rua: "", numero: "", bairro: "" });
      setPagamento({ forma: "", troco: "" });
      setTipoPedido("retirada");
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar pedido");
    }
  };

  // Renderização condicional: modoCliente mostra carrinho fixo, sem botão/flutuante/modal
  if (modoCliente && open === false) setOpen(true);

  return (
    <>
      {/* 🛒 Botão flutuante */}
      <Tooltip title={open ? "Fechar carrinho" : "Abrir carrinho"}>
        <IconButton className={classes.fabButton} onClick={() => setOpen(!open)}>

          <ShoppingCart style={{ fontSize: 30 }} />
          {itens.length > 0 && (
            <Box
              position="absolute"
              top={6}
              right={6}
              bgcolor="#ff3b30"
              color="#fff"
              borderRadius="50%"
              width={18}
              height={18}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="0.7rem"
              fontWeight="bold"
              boxShadow="0 0 4px rgba(0,0,0,0.4)"
              pointerEvents="none"
            >
              {itens.length}
            </Box>
          )}
        </IconButton>
      </Tooltip>

      {/* 🧾 Modal do carrinho */}
      <Collapse in={open}>
        <Paper className={classes.container}>
          <Typography variant="h6" style={{ fontSize: "1rem", marginBottom: 6 }}>
            {modoCliente ? "🛒 Monte seu pedido" : "🛒 Montar Pedido Manual"}
          </Typography>
          <Divider style={{ margin: "10px 0" }} />

          {/* Self-checkout required fields */}
          {modoCliente && (
            <Box mb={2}>
              <TextField
                label="Nome"
                variant="outlined"
                size="small"
                required
                fullWidth
                value={nomeCliente}
                onChange={(e) => setNomeCliente(e.target.value)}
                style={{ marginBottom: 10 }}
              />
              <TextField
                label="Telefone"
                variant="outlined"
                size="small"
                required
                fullWidth
                value={telefoneCliente}
                onChange={(e) => setTelefoneCliente(e.target.value)}
                style={{ marginBottom: 10 }}
              />
            </Box>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box className={classes.addBox}>
              {cardapioDia && opcoesCardapioValidas.length > 1 && (
                <>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionTitle}
                  >
                    🍽️ Cardápio do dia {cardapioDia.dia}
                  </Typography>
                  <TextField
                    select
                    label="Opção do cardápio"
                    variant="outlined"
                    size="small"
                    value={opcaoCardapio}
                    onChange={(e) => setOpcaoCardapio(e.target.value)}
                    helperText={
                      cardapioDia.titulo
                        ? cardapioDia.titulo
                        : "Selecione entre Opção 1 e 2"
                    }
                    fullWidth
                  >
                    {cardapioDia.opcao1_nome && (
                      <MenuItem value="1">Opção 1</MenuItem>
                    )}
                    {cardapioDia.opcao2_nome && (
                      <MenuItem value="2">Opção 2</MenuItem>
                    )}
                  </TextField>
                </>
              )}

              <TextField
                select
                label="Produto"
                variant="outlined"
                size="small"
                value={novoItem.nome}
                onChange={(e) => handleSelectProduto(e.target.value)}
                fullWidth
              >
                <MenuItem value="">
                  <em>Selecione...</em>
                </MenuItem>
                {produtos.map((p) => (
                  <MenuItem key={p.id} value={p.nome}>
                    {p.nome} — R$ {Number(p.valor || p.preco).toFixed(2)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Quantidade"
                type="number"
                variant="outlined"
                size="small"
                value={novoItem.quantidade}
                onChange={(e) =>
                  setNovoItem({ ...novoItem, quantidade: e.target.value })
                }
              />
              <TextField
                label="Preço (R$)"
                type="number"
                variant="outlined"
                size="small"
                value={novoItem.preco}
                onChange={(e) =>
                  setNovoItem({ ...novoItem, preco: e.target.value })
                }
              />
              <TextField
                label="Observações"
                variant="outlined"
                size="small"
                value={novoItem.observacoes}
                onChange={(e) =>
                  setNovoItem({ ...novoItem, observacoes: e.target.value })
                }
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAdd}
              >
                Adicionar
              </Button>
            </Box>
          )}

          {itens.map((item, i) => (
            <Box key={i} className={classes.item}>
              <Typography>
                {item.quantidade}x {item.nome} — R${" "}
                {(item.preco * item.quantidade).toFixed(2)}
              </Typography>
              <IconButton size="small" onClick={() => handleRemove(i)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {itens.length > 0 && (
            <>
              <Divider style={{ margin: "12px 0" }} />

              {/* 🚚 Tipo de Pedido */}
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                🚚 Tipo de Pedido
              </Typography>
              <Box className={classes.checkboxGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tipoPedido === "entrega"}
                      onChange={() => setTipoPedido("entrega")}
                      color="primary"
                    />
                  }
                  label="Entrega"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tipoPedido === "retirada"}
                      onChange={() => setTipoPedido("retirada")}
                      color="primary"
                    />
                  }
                  label="Retirada"
                />
              </Box>

              {/* 🏠 Endereço */}
              {tipoPedido === "entrega" && (
                <>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    🏠 Endereço de Entrega
                  </Typography>

                  <TextField
                    label="Rua"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={endereco.rua}
                    onChange={(e) => setEndereco({ ...endereco, rua: e.target.value })}
                    style={{ marginBottom: 10 }}
                  />
                  <TextField
                    label="Número"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={endereco.numero}
                    onChange={(e) =>
                      setEndereco({ ...endereco, numero: e.target.value })
                    }
                    style={{ marginBottom: 10 }}
                  />
                  <TextField
                    label="Bairro"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={endereco.bairro}
                    onChange={(e) =>
                      setEndereco({ ...endereco, bairro: e.target.value })
                    }
                  />

                  {endereco.bairro && (
                    <Typography
                      variant="body2"
                      style={{
                        color: "#444",
                        marginTop: 6,
                        fontWeight: 500,
                      }}
                    >
                      💸 Taxa de entrega: R$ {taxaEntrega.toFixed(2)}
                    </Typography>
                  )}
                </>
              )}

              <Divider style={{ margin: "12px 0" }} />

              {/* 💳 Pagamento */}
              <Typography variant="subtitle1" style={{ marginBottom: 6 }}>
                💳 Forma de Pagamento
              </Typography>

              <TextField
                select
                variant="outlined"
                size="small"
                fullWidth
                value={pagamento.forma}
                onChange={(e) =>
                  setPagamento({ ...pagamento, forma: e.target.value })
                }
                style={{ marginBottom: 10 }}
              >
                <MenuItem value="">Selecione...</MenuItem>
                <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                <MenuItem value="Cartão">Cartão</MenuItem>
                <MenuItem value="PIX">PIX</MenuItem>
              </TextField>

              {pagamento.forma === "Dinheiro" && (
                <TextField
                  label="Troco para quanto?"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={pagamento.troco}
                  onChange={(e) =>
                    setPagamento({ ...pagamento, troco: e.target.value })
                  }
                  style={{ marginBottom: 10 }}
                />
              )}

              <Typography className={classes.total}>
                Total: R$ {totalComTaxa.toFixed(2)}{" "}
                {tipoPedido === "entrega" && taxaEntrega > 0 && (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: 13,
                      color: "#555",
                    }}
                  >
                    (inclui R$ {taxaEntrega.toFixed(2)} de entrega)
                  </span>
                )}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={handleEnviarParaConfirmacao}
                className={classes.actionButton}
                style={{ backgroundColor: "#f97316", color: "white" }}
              >
                Enviar para confirmação
              </Button>

              <Button
                variant="contained"
                fullWidth
                onClick={handleFecharPedido}
                className={classes.actionButton}
                style={{ backgroundColor: "rgb(37, 211, 102)", color: "white" }}
              >
                Fechar Pedido
              </Button>
            </>
          )}
        </Paper>
      </Collapse>
    </>
  );
}
